import { EventType } from "@/analytics/analytics-events";
import { ExportToolClippingProperties } from "@/analytics/export-tool-events-properties";
import { useActiveCamera } from "@/components/r3f/cameras/active-camera-context";
import { CameraSwitch } from "@/components/ui/camera-switch";
import { selectSelectedExportModeTab } from "@/store/modes/export-mode-selectors";
import { useAppSelector } from "@/store/store-hooks";
import { useBoxControlsContext } from "@/utils/box-controls-context";
import {
  AutoClipCurrentViewIcon,
  FaroTooltip,
  ResetIcon,
  Toolbar,
  ToolButton,
  ToolGroup,
} from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import { Stack } from "@mui/material";
import { useMemo, type JSX } from "react";
import { ExportModeTab } from "./export-mode-types";

/**
 * @returns the overlay for the export mode
 */
export function ExportModeOverlay(): JSX.Element | null {
  const { autoBoxEvent, resetBoxEvent } = useBoxControlsContext();

  // Setup properties for analytics events
  const selectedTab = useAppSelector(selectSelectedExportModeTab);
  const via = useMemo<ExportToolClippingProperties["via"]>(() => {
    if (selectedTab === "pointCloud") {
      return "point cloud";
    }
    return "orthophoto";
  }, [selectedTab]);

  const {
    cameraProjection,
    setCameraProjection,
    isProjectionSwitchVisible,
    predefinedView,
    setPredefinedView,
  } = useActiveCamera();

  if (selectedTab === ExportModeTab.scans) {
    // No overlay for scans export tab required
    return null;
  }

  return (
    <Stack
      // Use row-reverse to make the reset button appear on the right
      direction="row-reverse"
      sx={{
        justifyContent: "space-between",
        height: "100%",
        width: "100%",
      }}
    >
      <Stack sx={{ justifyContent: "center" }}>
        <Toolbar>
          <ToolGroup>
            <FaroTooltip
              title="Reset clipping box"
              placement="left"
              aria-label="reset clipping box"
            >
              <ToolButton
                onClick={() => {
                  Analytics.track<ExportToolClippingProperties>(
                    EventType.exportResetClip,
                    {
                      via,
                    },
                  );
                  resetBoxEvent.emit();
                }}
              >
                <ResetIcon sx={{ width: "24px", height: "24px" }} />
              </ToolButton>
            </FaroTooltip>
            <FaroTooltip title="Auto-clip to current view">
              <ToolButton
                onClick={() => {
                  Analytics.track<ExportToolClippingProperties>(
                    EventType.exportAutoClip,
                    {
                      via,
                    },
                  );
                  autoBoxEvent.emit();
                }}
              >
                <AutoClipCurrentViewIcon />
              </ToolButton>
            </FaroTooltip>
          </ToolGroup>
        </Toolbar>
      </Stack>
      {isProjectionSwitchVisible && (
        <Stack direction="column" spacing="1em" sx={{ justifyContent: "end" }}>
          <CameraSwitch
            cameraOptions3D={cameraProjection}
            onCameraChange={setCameraProjection}
            showPredefinedViewsOptions
            predefinedView={predefinedView}
            onPredefinedViewSelected={setPredefinedView}
          />
        </Stack>
      )}
    </Stack>
  );
}
